import { useQuery } from '@tanstack/react-query';
import { useState, useEffect, useMemo } from 'react';
import { apiClient } from '../api/client';

// Unified search result type matching backend response
export interface SearchResult {
  type: 'collection' | 'wallet' | 'nft';
  id: string;
  name: string;
  slug?: string | null; // Collection slug for friendly URLs
  subtitle: string | null;
  image_url: string | null;
  relevance: number;
  // Additional fields based on type
  is_verified?: boolean;
  floor_price?: number | null;
  total_nfts_held?: number | null;
  collection_name?: string | null;
}

export interface GlobalSearchResults {
  results: SearchResult[];
}

interface GlobalSearchResponse {
  success: boolean;
  data?: GlobalSearchResults;
  error?: string;
}

// Query key factory for search
export const globalSearchKeys = {
  search: (query: string) => ['globalSearch', query] as const,
};

/**
 * Hook for debouncing a value
 * @param value - The value to debounce
 * @param delay - Debounce delay in milliseconds
 */
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
}

/**
 * Global search hook with debouncing
 *
 * Searches across collections, wallets, and NFTs with a 300ms debounce.
 * Returns a unified list sorted by relevance.
 *
 * @param query - Search query string
 * @param options - Optional configuration
 * @returns Query result with unified search results
 */
export function useGlobalSearch(
  query: string,
  options: {
    debounceMs?: number;
    limit?: number;
    enabled?: boolean;
  } = {}
) {
  const { debounceMs = 300, limit = 12, enabled = true } = options;

  // Debounce the search query
  const debouncedQuery = useDebounce(query.trim(), debounceMs);

  // Only search if query is at least 2 characters
  const shouldSearch = enabled && debouncedQuery.length >= 2;

  const queryResult = useQuery({
    queryKey: globalSearchKeys.search(debouncedQuery),
    queryFn: async (): Promise<GlobalSearchResults> => {
      const params = new URLSearchParams({
        q: debouncedQuery,
        limit: String(limit),
      });

      const response = await apiClient.get<GlobalSearchResponse>(
        `/search?${params.toString()}`
      );

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Search failed');
      }

      return response.data;
    },
    enabled: shouldSearch,
    staleTime: 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
  });

  // Compute whether results are empty
  const isEmpty = useMemo(() => {
    if (!queryResult.data) return true;
    return queryResult.data.results.length === 0;
  }, [queryResult.data]);

  // Compute total results count
  const totalResults = useMemo(() => {
    if (!queryResult.data) return 0;
    return queryResult.data.results.length;
  }, [queryResult.data]);

  return {
    ...queryResult,
    results: queryResult.data?.results ?? [],
    debouncedQuery,
    isEmpty,
    totalResults,
    isSearching: shouldSearch && queryResult.isFetching,
  };
}
